import { useEffect } from "react";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { PASTE_COMMAND, COMMAND_PRIORITY_LOW, $insertNodes, LexicalEditor } from "lexical";
import { $generateNodesFromDOM } from "@lexical/html";
import { getFilesFromClipboard } from "@/common/helpers/files";

type OnPastePluginProps = {
  onPaste: (pastedHtml: string, pastedText: string) => string | null;
  onAfterPaste: (editor: LexicalEditor) => void;
  onPastedFiles?: (files: File[]) => void;
};

export function OnPastePlugin({ onPaste, onAfterPaste, onPastedFiles }: OnPastePluginProps) {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    return editor.registerCommand(
      PASTE_COMMAND,
      (event: ClipboardEvent) => {
        const pastedHtml = event.clipboardData?.getData("text/html") ?? "";

        if (onPastedFiles && !pastedHtml.trim()) {
          const pastedFiles = getFilesFromClipboard(event.clipboardData);
          if (pastedFiles.length > 0) {
            onPastedFiles(pastedFiles);
            event.preventDefault();

            return true;
          }
        }

        const pastedText = event.clipboardData?.getData("text/plain") ?? "";

        const newHtml = onPaste(pastedHtml, pastedText);

        if (newHtml === null) {
          return false; // let default paste behavior run
        }

        event.preventDefault();

        editor.update(
          () => {
            const parser = new DOMParser();
            const dom = parser.parseFromString(newHtml, "text/html");
            const nodes = $generateNodesFromDOM(editor, dom);
            $insertNodes(nodes);
          },
          {
            onUpdate: () => onAfterPaste(editor),
          }
        );

        return true;
      },
      COMMAND_PRIORITY_LOW
    );
  }, [editor, onPaste, onAfterPaste, onPastedFiles]);

  return null;
}
